import {
    WIZARD_MAX_OPTIONS,
    WIZARD_MIN_COVERAGE,
    WIZARD_QUESTIONS,
    WIZARD_RESULT_LIMIT,
    WIZARD_SKIPPED,
} from "../data/exerciseWizardConfig";
import {
    WIZARD_PROFILE_DEFAULTS_BY_EQUIPMENT,
    WIZARD_PROFILE_OVERRIDES_BY_ID,
} from "../data/exerciseWizardProfiles";

const PRIMARY_TO_MAIN_AREA = {
    chest: "chest",
    upper_chest: "chest",
    lower_chest: "chest",
    back: "back",
    lats: "back",
    traps: "back",
    rhomboids: "back",
    lower_back: "back",
    erector_spinae: "back",
    deltoids: "shoulders",
    rear_delts: "shoulders",
    shoulders: "shoulders",
    biceps: "arms",
    triceps: "arms",
    forearms: "arms",
    brachialis: "arms",
    quadriceps: "legs",
    hamstrings: "legs",
    glutes: "legs",
    calves: "legs",
    adductors: "legs",
    abductors: "legs",
    hip_flexors: "legs",
    legs: "legs",
    abs: "core",
    obliques: "core",
    core: "core",
};

function normalizeText(value) {
    return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function matchesAnyPattern(text, patterns) {
    return patterns.some((pattern) => text.includes(pattern));
}

function getPrimaryMuscleGroup(exercise) {
    if (!Array.isArray(exercise?.muscleGroups)) {
        return "";
    }

    const primary = exercise.muscleGroups.find((group) => group?.priority === "primary");
    return String(primary?.group || exercise.muscleGroups[0]?.group || "").toLowerCase();
}

function inferMainArea(exercise) {
    const primaryGroup = getPrimaryMuscleGroup(exercise);
    return PRIMARY_TO_MAIN_AREA[primaryGroup] || null;
}

function inferSupport(exercise) {
    const text = normalizeText(`${exercise?.name || ""} ${exercise?.id || ""}`);

    if (!text) {
        return null;
    }

    if (matchesAnyPattern(text, ["bench", "incline", "decline", "preacher", "chest supported", "hip thrust"])) {
        return "bench";
    }

    if (matchesAnyPattern(text, ["seated", "seat"])) {
        return "seat";
    }

    if (matchesAnyPattern(text, ["floor"])) {
        return "floor";
    }

    if (matchesAnyPattern(text, ["hanging", "pull up", "chin up"])) {
        return "hanging";
    }

    if (matchesAnyPattern(text, ["standing"])) {
        return "standing";
    }

    return null;
}

function inferMovement(exercise) {
    const text = normalizeText(`${exercise?.name || ""} ${exercise?.id || ""}`);

    if (!text) {
        return null;
    }

    if (
        matchesAnyPattern(text, [
            "carry",
            "plank",
            "crunch",
            "sit up",
            "jackknife",
            "rollout",
            "pallof",
            "woodchop",
            "twist",
            "side bend",
            "dead bug",
            "v up",
            "leg raise",
            "mountain climber",
            "hollow",
            "body saw",
            "stir the pot",
        ])
    ) {
        return "core_or_carry";
    }

    if (matchesAnyPattern(text, ["squat", "lunge", "step up", "leg press", "pistol"])) {
        return "squat_or_lunge";
    }

    if (matchesAnyPattern(text, ["deadlift", "good morning", "hip thrust", "glute bridge", "pull through"])) {
        return "hinge";
    }

    if (matchesAnyPattern(text, ["press", "push up", "dip"])) {
        return "press";
    }

    if (matchesAnyPattern(text, ["row", "pulldown", "pull up", "chin up", "face pull"])) {
        return "pull";
    }

    if (
        matchesAnyPattern(text, [
            "curl",
            "extension",
            "raise",
            "fly",
            "shrug",
            "kickback",
            "adduction",
            "abduction",
            "calf raise",
        ])
    ) {
        return "raise_or_curl";
    }

    return null;
}

export function buildExerciseWizardProfile(exercise) {
    const defaults = WIZARD_PROFILE_DEFAULTS_BY_EQUIPMENT[exercise?.equipment] || {};
    const inferred = {
        mainArea: inferMainArea(exercise),
        support: inferSupport(exercise),
        movement: inferMovement(exercise),
    };
    const explicitProfile = exercise?.wizardProfile || {};
    const overrides = WIZARD_PROFILE_OVERRIDES_BY_ID[exercise?.id] || {};

    return {
        equipmentFamily: null,
        implement: null,
        support: null,
        movement: null,
        mainArea: null,
        ...defaults,
        ...inferred,
        ...explicitProfile,
        ...overrides,
    };
}

export function enrichExercisesForWizard(exercises = []) {
    return exercises.map((exercise) => ({
        ...exercise,
        wizardProfile: buildExerciseWizardProfile(exercise),
    }));
}

export function applyWizardAnswers(exercises = [], answers = {}) {
    const activeAnswers = Object.entries(answers).filter(([, value]) => value && value !== WIZARD_SKIPPED);

    if (activeAnswers.length === 0) {
        return exercises;
    }

    return exercises.filter((exercise) => {
        const profile = exercise?.wizardProfile || buildExerciseWizardProfile(exercise);

        return activeAnswers.every(([field, value]) => {
            if (field === "muscleGroup") {
                const normalizedValue = String(value || "").toLowerCase();

                return Array.isArray(exercise?.muscleGroups) && exercise.muscleGroups.some(
                    (group) => String(group?.group || "").toLowerCase() === normalizedValue
                );
            }

            return profile?.[field] === value;
        });
    });
}

function sortExercisesByRecent(exercises = [], lastUsedByExercise = {}) {
    return [...exercises].sort((a, b) => {
        const aDate = String(lastUsedByExercise?.[a.id] || "");
        const bDate = String(lastUsedByExercise?.[b.id] || "");

        if (aDate && bDate && aDate !== bDate) {
            return bDate.localeCompare(aDate);
        }

        if (aDate && !bDate) {
            return -1;
        }

        if (!aDate && bDate) {
            return 1;
        }

        return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
}

function questionApplies(question, answers) {
    if (!Array.isArray(question?.showWhen) || question.showWhen.length === 0) {
        return true;
    }

    return question.showWhen.every((condition) => {
        const answer = answers?.[condition.field];
        return answer && answer !== WIZARD_SKIPPED && condition.in.includes(answer);
    });
}

function buildQuestionOptions(question, exercises) {
    const counts = question.values.reduce((acc, value) => {
        acc[value] = 0;
        return acc;
    }, {});

    exercises.forEach((exercise) => {
        const value = exercise?.wizardProfile?.[question.id];
        if (value && Object.prototype.hasOwnProperty.call(counts, value)) {
            counts[value] += 1;
        }
    });

    const options = question.values
        .filter((value) => counts[value] > 0)
        .map((value) => ({
            value,
            count: counts[value],
        }));

    const knownCount = options.reduce((sum, option) => sum + option.count, 0);
    const coverage = exercises.length > 0 ? knownCount / exercises.length : 0;

    return {
        coverage,
        options,
    };
}

export function getNextWizardQuestion(exercises = [], answers = {}) {
    const preparedExercises = exercises.map((exercise) =>
        exercise?.wizardProfile
            ? exercise
            : {
                ...exercise,
                wizardProfile: buildExerciseWizardProfile(exercise),
            }
    );

    for (const question of WIZARD_QUESTIONS) {
        if (Object.prototype.hasOwnProperty.call(answers, question.id)) {
            continue;
        }

        if (!questionApplies(question, answers)) {
            continue;
        }

        const { coverage, options } = buildQuestionOptions(question, preparedExercises);

        if (coverage < WIZARD_MIN_COVERAGE) {
            continue;
        }

        if (options.length < 2 || options.length > WIZARD_MAX_OPTIONS) {
            continue;
        }

        return {
            id: question.id,
            titleKey: question.titleKey,
            optionKeyPrefix: question.optionKeyPrefix,
            options,
        };
    }

    return null;
}

export function getWizardState({ exercises = [], answers = {}, lastUsedByExercise = {} }) {
    const preparedExercises = enrichExercisesForWizard(exercises);
    const remainingExercises = sortExercisesByRecent(
        applyWizardAnswers(preparedExercises, answers),
        lastUsedByExercise
    );
    const remainingCount = remainingExercises.length;
    const nextQuestion =
        remainingCount > WIZARD_RESULT_LIMIT ? getNextWizardQuestion(remainingExercises, answers) : null;

    return {
        answers,
        remainingCount,
        remainingExercises,
        nextQuestion,
        isComplete: remainingCount <= WIZARD_RESULT_LIMIT || nextQuestion === null,
    };
}
